import { Badge, Button, Card, cn, Spinner } from '@fc/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell, Check, CheckCheck } from 'lucide-react'
import { type KeyboardEvent, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  NOTIFICATION_KIND_LABELS,
  type NotificationDto,
} from '../../lib/notifications-api'
import { KIND_ICONS, timeAgo } from './shared'
import { UNREAD_COUNT_KEY } from './unread-indicator'

export function NotificationsList() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [unreadOnly, setUnreadOnly] = useState(false)

  const listKey = ['notifications', 'list', { unreadOnly }] as const
  const { data, isLoading, isError, error } = useQuery({
    queryKey: listKey,
    queryFn: () => listNotifications({ unreadOnly, pageSize: 50 }),
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['notifications'] })
    queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY })
  }

  const markRead = useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: invalidate,
  })

  const markAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: invalidate,
  })

  const items = data?.items ?? []
  const hasUnread = items.some((n) => !n.readAt)

  const open = (n: NotificationDto) => {
    if (!n.readAt) markRead.mutate(n.id)
    // Only follow same-origin in-app paths (the API stores app-relative links).
    if (n.link && n.link.startsWith('/')) navigate(n.link)
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifications"
        description="Your alerts, invitations, and updates."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant={unreadOnly ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setUnreadOnly((v) => !v)}
            >
              {unreadOnly ? 'Showing unread' : 'Show all'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => markAll.mutate()}
              disabled={!hasUnread || markAll.isPending}
            >
              {markAll.isPending ? <Spinner /> : <CheckCheck />}
              Mark all read
            </Button>
          </div>
        }
      />

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-muted">
            <Spinner /> Loading notifications…
          </div>
        ) : isError ? (
          <div className="space-y-2 py-20 text-center">
            <p className="text-sm text-danger">Couldn’t load notifications.</p>
            <p className="text-xs text-dim">
              {error instanceof Error ? error.message : 'Is the API running on :3000?'}
            </p>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-20 text-center">
            <div className="grid size-12 place-items-center rounded-full bg-card text-secondary">
              <Bell className="size-6" />
            </div>
            <p className="max-w-sm text-sm text-muted">
              {unreadOnly ? 'No unread notifications.' : 'You have no notifications yet.'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border-soft">
            {items.map((n) => {
              const Icon = KIND_ICONS[n.kind] ?? Bell
              const clickable = Boolean(n.link && n.link.startsWith('/'))
              return (
                <li
                  key={n.id}
                  className={cn(
                    'flex items-start gap-3 px-5 py-4 transition-colors',
                    !n.readAt && 'bg-card/60',
                    clickable && 'cursor-pointer hover:bg-card-hover',
                  )}
                  {...(clickable
                    ? {
                        role: 'link' as const,
                        tabIndex: 0,
                        onClick: () => open(n),
                        onKeyDown: (e: KeyboardEvent) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            open(n)
                          }
                        },
                      }
                    : {})}
                >
                  <div
                    className={cn(
                      'mt-0.5 grid size-9 shrink-0 place-items-center rounded-full',
                      n.readAt ? 'bg-card text-dim' : 'bg-card text-secondary',
                    )}
                  >
                    <Icon className="size-4" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={cn('truncate text-sm', !n.readAt && 'font-semibold')}>
                        {n.title}
                      </span>
                      {!n.readAt && (
                        <span
                          aria-hidden="true"
                          className="size-2 shrink-0 rounded-full bg-secondary"
                        />
                      )}
                    </div>
                    {n.body && <p className="mt-0.5 text-sm text-muted">{n.body}</p>}
                    <div className="mt-1 flex items-center gap-2 text-xs text-dim">
                      <Badge variant="neutral">{NOTIFICATION_KIND_LABELS[n.kind] ?? n.kind}</Badge>
                      <span>{timeAgo(n.createdAt)}</span>
                    </div>
                  </div>

                  {!n.readAt && (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Mark as read"
                      title="Mark as read"
                      disabled={markRead.isPending}
                      onClick={(e) => {
                        e.stopPropagation()
                        markRead.mutate(n.id)
                      }}
                    >
                      <Check />
                    </Button>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </Card>
    </div>
  )
}
